/**
 * useOpenSlideout — THE way a click opens a slideout.
 *
 * The product rule: a click FROM THE APP (a page, a list, the chat, a
 * settings section, the proposal panel) REPLACES whatever slideouts are
 * layered; only a click from INSIDE a rendered entity / email-thread
 * slideout builds the stack (drill-down keeps back-history).
 *
 * The returned function resolves the click's origin from
 * `SlideoutOriginContext` (declared by the stack-entry renderers) and
 * dispatches:
 *
 *  - origin is an entity / email-thread slideout → `pushSlideout` (layer;
 *    back returns to the record you came from)
 *  - origin is a proposal → `openSlideout` with the proposal as `base`
 *    (the proposal panel behaves like the app: the clicked record replaces
 *    anything layered above the proposal, which itself stays put)
 *  - no origin (app surface) → `openSlideout` (replaces the whole stack)
 *
 * Reach for raw `pushSlideout` only for programmatic, non-click flows
 * (e.g. AutoOpenFirstRecord's stand-down-if-anything-open auto-open).
 */

import { useCallback } from 'react';

// Direct module paths, NOT the `lib/crm/hooks` barrel — same cycle-avoidance
// reason as useSlideoutStack / useNavigateToEntity.
import { useSlideoutOrigin } from '../contexts/SlideoutOriginContext';
import {
  type PushSlideoutOptions,
  type SlideoutPushInput,
  useSlideoutStackContextOptional,
} from '../contexts/SlideoutStackContext';

export type OpenSlideoutFn = (
  input: SlideoutPushInput,
  options?: PushSlideoutOptions
) => void;

/**
 * Optional variant: returns `undefined` outside a `SlideoutStackProvider`
 * (e.g. Storybook), mirroring `useSlideoutStackContextOptional`. Callers
 * that used `slideoutCtx?.pushSlideout` migrate to `open?.(...)`.
 */
export function useOpenSlideoutOptional(): OpenSlideoutFn | undefined {
  const ctx = useSlideoutStackContextOptional();
  const origin = useSlideoutOrigin();
  const pushSlideout = ctx?.pushSlideout;
  const openSlideout = ctx?.openSlideout;

  const open = useCallback<OpenSlideoutFn>(
    (input, options) => {
      if (!pushSlideout || !openSlideout) {
        return;
      }
      if (origin && origin.kind !== 'proposal') {
        // Click from inside a rendered entity / email-thread slideout —
        // drill-down layers, preserving back-history.
        pushSlideout(input, options);
      } else if (origin) {
        // Click from inside the proposal panel — app-like: replace whatever
        // is layered above the proposal, keep the proposal underneath.
        openSlideout(input, { ...options, base: origin });
      } else {
        // App-level click — replace the whole stack.
        openSlideout(input, options);
      }
    },
    [pushSlideout, openSlideout, origin]
  );

  return pushSlideout && openSlideout ? open : undefined;
}

/** Throwing variant for components that always render under the provider. */
export function useOpenSlideout(): OpenSlideoutFn {
  const open = useOpenSlideoutOptional();
  if (!open) {
    throw new Error(
      'useOpenSlideout must be used within a SlideoutStackProvider'
    );
  }
  return open;
}
